import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { loginApi } from '@/app/login/api';
import { useSession } from '@/app/login/session';
import { LoginFrame, LOGIN_INPUT, LOGIN_PRIMARY } from '@/app/login/LoginFrame';

type Step = { kind: 'password' } | { kind: 'code'; challengeId: string; expiresAt: string };

/** 돌아갈 화면은 같은 사이트 안의 경로만 받는다 — `next=https://…`로 바깥 주소에 보내지 않게. */
function safeNext(raw: string | null): string {
  return raw && raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/login') ? raw : '/';
}

/**
 * 로그인 — 1단계 이메일·비밀번호, 2단계 메일로 받은 코드. 코드 단계의 식별자는 화면 상태에만 둔다(새로고침하면
 * 처음부터 다시 — 코드도 새로 받는다).
 */
export function LoginView(): React.JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { session, reload } = useSession();
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (session?.account) return <Navigate to={next} replace />;

  const run = (action: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    action()
      .catch((cause: Error) => setError(cause.message.replace(/^요청 실패 \(HTTP \d+\) — /, '')))
      .finally(() => setBusy(false));
  };

  const submitPassword = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    run(async () => {
      const issued = await loginApi.start(email.trim(), password);
      setPassword('');
      setStep({ kind: 'code', ...issued });
    });
  };

  const submitCode = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (step.kind !== 'code') return;
    run(async () => {
      try {
        await loginApi.verify(step.challengeId, code);
      } catch (cause) {
        // 코드를 다 틀렸거나 만료됐으면 이 단계로는 더 못 간다 — 비밀번호부터 다시.
        if (cause instanceof Error && cause.message.includes('처음부터')) {
          setStep({ kind: 'password' });
          setCode('');
        }
        throw cause;
      }
      await reload();
      navigate(next, { replace: true });
    });
  };

  return (
    <LoginFrame title="로그인" hint={step.kind === 'password' ? '콘솔 계정 이메일과 비밀번호' : `${email}로 보낸 인증 코드`}>
      {step.kind === 'password' ? (
        <form onSubmit={submitPassword} className="flex flex-col gap-2.5">
          <input
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="이메일"
            className={LOGIN_INPUT}
          />
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="비밀번호"
            className={LOGIN_INPUT}
          />
          {error && <p className="text-[12px] text-rose-700">{error}</p>}
          <button type="submit" disabled={busy} className={LOGIN_PRIMARY}>
            {busy ? '확인 중…' : '다음'}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="flex flex-col gap-2.5">
          <input
            autoComplete="one-time-code"
            required
            autoFocus
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
            placeholder="XXXX-XXXX-XX"
            maxLength={14}
            className={`${LOGIN_INPUT} text-center font-mono tracking-[0.2em]`}
          />
          <p className="text-[11.5px] text-muted">
            {new Date(step.expiresAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}까지 유효합니다. 메일이 보이지 않으면 스팸함을 확인해 주세요.
          </p>
          {error && <p className="text-[12px] text-rose-700">{error}</p>}
          <button type="submit" disabled={busy} className={LOGIN_PRIMARY}>
            {busy ? '확인 중…' : '로그인'}
          </button>
          <button
            type="button"
            onClick={() => {
              setStep({ kind: 'password' });
              setCode('');
              setError(null);
            }}
            className="cursor-pointer text-[12px] text-muted hover:underline"
          >
            처음부터 다시
          </button>
        </form>
      )}
    </LoginFrame>
  );
}
